import React from 'react';
import { Group, Line, Rect, Text } from 'react-konva';
import { DIM_COLOR } from '../constants';

/**
 * Renders dimension lines (cotas) around the plant.
 *
 * Strategy: Show only the TOTAL envelope width/height plus
 * individual room widths (top) and heights (left).
 * Segments smaller than MIN_SEGMENT_PX are merged to avoid clutter.
 */
const MIN_SEGMENT_VIRTUAL = 50; // Min virtual units to show a segment (avoid tiny labels)
const LABEL_W = 44;
const LABEL_H = 15;
const FONT_SIZE = 9;

export default function DimensionLines({ rooms, extW, extH, offset, sc, visible }) {
  if (!visible || !rooms || rooms.length === 0) return null;

  const ox = offset.x;
  const oy = offset.y;
  const s = (v) => v * sc;

  const elements = [];

  // ── Helper: render a horizontal dimension ──
  const hDim = (key, x1px, x2px, yPx, labelText) => {
    const midX = (x1px + x2px) / 2;
    return (
      <Group key={key}>
        <Line points={[x1px, yPx, x2px, yPx]} stroke={DIM_COLOR} strokeWidth={1} listening={false} />
        <Line points={[x1px, yPx - 4, x1px, yPx + 4]} stroke={DIM_COLOR} strokeWidth={1} listening={false} />
        <Line points={[x2px, yPx - 4, x2px, yPx + 4]} stroke={DIM_COLOR} strokeWidth={1} listening={false} />
        <Rect
          x={midX - LABEL_W / 2}
          y={yPx - LABEL_H / 2}
          width={LABEL_W}
          height={LABEL_H}
          fill={DIM_COLOR}
          cornerRadius={3}
          listening={false}
        />
        <Text
          x={midX - LABEL_W / 2}
          y={yPx - LABEL_H / 2 + 1}
          width={LABEL_W}
          height={LABEL_H}
          text={labelText}
          fontSize={FONT_SIZE}
          fontFamily="Inter, sans-serif"
          fontStyle="600"
          fill="#fff"
          align="center"
          verticalAlign="middle"
          listening={false}
        />
      </Group>
    );
  };

  // ── Helper: render a vertical dimension ──
  const vDim = (key, y1px, y2px, xPx, labelText) => {
    const midY = (y1px + y2px) / 2;
    return (
      <Group key={key}>
        <Line points={[xPx, y1px, xPx, y2px]} stroke={DIM_COLOR} strokeWidth={1} listening={false} />
        <Line points={[xPx - 4, y1px, xPx + 4, y1px]} stroke={DIM_COLOR} strokeWidth={1} listening={false} />
        <Line points={[xPx - 4, y2px, xPx + 4, y2px]} stroke={DIM_COLOR} strokeWidth={1} listening={false} />
        <Rect
          x={xPx - LABEL_W / 2}
          y={midY - LABEL_H / 2}
          width={LABEL_W}
          height={LABEL_H}
          fill={DIM_COLOR}
          cornerRadius={3}
          listening={false}
        />
        <Text
          x={xPx - LABEL_W / 2}
          y={midY - LABEL_H / 2 + 1}
          width={LABEL_W}
          height={LABEL_H}
          text={labelText}
          fontSize={FONT_SIZE}
          fontFamily="Inter, sans-serif"
          fontStyle="600"
          fill="#fff"
          align="center"
          verticalAlign="middle"
          listening={false}
        />
      </Group>
    );
  };

  // Helper to compute real meters from virtual bbox + known area
  const realDim = (aw, ah, area) => {
    if (!area || !aw || !ah) return { wm: 0, hm: 0 };
    const aspect = aw / ah;
    const wm = Math.sqrt(area * aspect);
    const hm = area / wm;
    return { wm, hm };
  };

  // ── Total envelope dimensions ──
  const totalLabelOffset = 40; // px from edge

  // Total width (top)
  {
    const x1 = ox;
    const x2 = ox + s(extW);
    const y = oy - totalLabelOffset;

    // Compute total area from rooms
    const totalArea = rooms.reduce((sum, r) => sum + (r.area || 0), 0);
    // Use envelope extW/extH ratio with total area for approximate total meters
    const { wm } = realDim(extW, extH, totalArea);
    const label = wm > 0 ? `${wm.toFixed(1)}m` : `${(extW / 100).toFixed(1)}m`;

    elements.push(hDim('dim-total-w', x1, x2, y, label));
  }

  // Total height (left)
  {
    const y1 = oy;
    const y2 = oy + s(extH);
    const x = ox - totalLabelOffset;

    const totalArea = rooms.reduce((sum, r) => sum + (r.area || 0), 0);
    const { hm } = realDim(extW, extH, totalArea);
    const label = hm > 0 ? `${hm.toFixed(1)}m` : `${(extH / 100).toFixed(1)}m`;

    elements.push(vDim('dim-total-h', y1, y2, x, label));
  }

  // ── Individual room dimensions (top row + left column) ──
  // Only show rooms that are wide/tall enough to display a readable label
  const segmentOffset = 18; // px from plant edge

  // Top segments: each room's width
  const topRooms = [...rooms]
    .filter(r => r.aw >= MIN_SEGMENT_VIRTUAL)
    .sort((a, b) => a.ax - b.ax);

  // Deduplicate: if two rooms overlap horizontally, only show one
  const shownTopRanges = [];
  topRooms.forEach((r) => {
    const x1 = r.ax;
    const x2 = r.ax + r.aw;
    // Check overlap with already shown
    const overlaps = shownTopRanges.some(([sx1, sx2]) => {
      const overlapStart = Math.max(x1, sx1);
      const overlapEnd = Math.min(x2, sx2);
      return (overlapEnd - overlapStart) > MIN_SEGMENT_VIRTUAL * 0.5;
    });
    if (!overlaps) {
      shownTopRanges.push([x1, x2]);
      const { wm } = realDim(r.aw, r.ah, r.area);
      const label = wm > 0 ? `${wm.toFixed(2)}m` : '';
      if (label) {
        elements.push(hDim(
          `dim-room-w-${r.id}`,
          ox + s(r.ax),
          ox + s(r.ax + r.aw),
          oy - segmentOffset,
          label,
        ));
      }
    }
  });

  // Left segments: each room's height
  const leftRooms = [...rooms]
    .filter(r => r.ah >= MIN_SEGMENT_VIRTUAL)
    .sort((a, b) => a.ay - b.ay);

  const shownLeftRanges = [];
  leftRooms.forEach((r) => {
    const y1 = r.ay;
    const y2 = r.ay + r.ah;
    const overlaps = shownLeftRanges.some(([sy1, sy2]) => {
      const overlapStart = Math.max(y1, sy1);
      const overlapEnd = Math.min(y2, sy2);
      return (overlapEnd - overlapStart) > MIN_SEGMENT_VIRTUAL * 0.5;
    });
    if (!overlaps) {
      shownLeftRanges.push([y1, y2]);
      const { hm } = realDim(r.aw, r.ah, r.area);
      const label = hm > 0 ? `${hm.toFixed(2)}m` : '';
      if (label) {
        elements.push(vDim(
          `dim-room-h-${r.id}`,
          oy + s(r.ay),
          oy + s(r.ay + r.ah),
          ox - segmentOffset,
          label,
        ));
      }
    }
  });

  return <>{elements}</>;
}
